import React from 'react';
import { Card, Descriptions, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import type {
  InspectionReleaseNote,
  IrnCustomerSnapshot,
  IrnDeliveryNoteItemSnapshot,
  IrnFirLineResultSnapshot,
} from '@/services/qc-workflow.service';

const { Text, Paragraph } = Typography;

function joinParts(parts: (string | null | undefined | number)[]): string {
  return parts.filter((p) => p != null && String(p).trim() !== '').join(', ');
}

export function formatCustomerShippingBlock(c: IrnCustomerSnapshot | null | undefined): string {
  if (!c) return '';
  const line12 = [c.shipping_address_line1, c.shipping_address_line2].filter(Boolean).join(', ');
  const city = joinParts([c.shipping_city, c.shipping_state, c.shipping_postal_code, c.shipping_country]);
  return [line12, city].filter(Boolean).join('\n');
}

export function formatCustomerBillingBlock(c: IrnCustomerSnapshot | null | undefined): string {
  if (!c) return '';
  const line12 = [c.billing_address_line1, c.billing_address_line2].filter(Boolean).join(', ');
  const city = joinParts([c.billing_city, c.billing_state, c.billing_postal_code, c.billing_country]);
  return [line12, city].filter(Boolean).join('\n');
}

const deliveryLineColumns: ColumnsType<IrnDeliveryNoteItemSnapshot> = [
  { title: 'Line', dataIndex: 'line_number', key: 'line_number', width: 64 },
  { title: 'Item code', dataIndex: 'item_code', key: 'item_code', width: 110, ellipsis: true },
  {
    title: 'Description',
    dataIndex: 'item_description',
    key: 'item_description',
    ellipsis: true,
    render: (t: string) => t || '—',
  },
  {
    title: 'Delivered qty',
    dataIndex: 'delivered_quantity',
    key: 'delivered_quantity',
    width: 110,
    align: 'right',
    render: (q: number) => (q != null && !Number.isNaN(Number(q)) ? Number(q).toFixed(4) : '—'),
  },
  { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 72 },
  { title: 'HSN', dataIndex: 'hsn_code', key: 'hsn_code', width: 90, ellipsis: true },
  {
    title: 'Batch',
    dataIndex: 'batch_numbers',
    key: 'batch_numbers',
    width: 120,
    ellipsis: true,
    render: (t: string) => t || '—',
  },
  {
    title: 'Serial',
    dataIndex: 'serial_numbers',
    key: 'serial_numbers',
    width: 120,
    ellipsis: true,
    render: (t: string) => t || '—',
  },
  {
    title: 'Notes',
    dataIndex: 'notes',
    key: 'notes',
    ellipsis: true,
    render: (t: string) => t || '—',
  },
];

const firLineColumns: ColumnsType<IrnFirLineResultSnapshot> = [
  {
    title: 'WO line',
    key: 'ln',
    width: 80,
    render: (_, r) => r.workOrderLineItem?.line_number ?? '—',
  },
  {
    title: 'Item code',
    key: 'code',
    width: 100,
    ellipsis: true,
    render: (_, r) => r.workOrderLineItem?.item_code || '—',
  },
  {
    title: 'Description',
    key: 'desc',
    ellipsis: true,
    render: (_, r) => r.workOrderLineItem?.item_description || '—',
  },
  {
    title: 'Approved',
    dataIndex: 'approved_quantity',
    key: 'approved_quantity',
    width: 100,
    align: 'right',
    render: (q: number) => (q != null && !Number.isNaN(Number(q)) ? Number(q).toFixed(4) : '—'),
  },
  {
    title: 'Rejected',
    dataIndex: 'rejected_quantity',
    key: 'rejected_quantity',
    width: 100,
    align: 'right',
    render: (q: number) => (q != null && !Number.isNaN(Number(q)) ? Number(q).toFixed(4) : '—'),
  },
  {
    title: 'Line rejection',
    dataIndex: 'rejection_reason',
    key: 'rejection_reason',
    ellipsis: true,
    render: (t: string) => t || '—',
  },
];

export function IrnInspectorDetailsScreen({ irn }: { irn: InspectionReleaseNote }) {
  const dn = irn.deliveryNote;
  const cust = dn?.customer;
  const items = dn?.items ?? [];
  const fir = irn.fir;

  return (
    <>
      {dn ? (
        <Card title="Customer & delivery (from delivery note)">
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Customer" span={2}>
              <Text strong>{cust?.customer_name || '—'}</Text>
              {cust?.customer_code ? <Text type="secondary"> ({cust.customer_code})</Text> : null}
            </Descriptions.Item>
            <Descriptions.Item label="Contact person">{cust?.contact_person || '—'}</Descriptions.Item>
            <Descriptions.Item label="Email">{cust?.email || '—'}</Descriptions.Item>
            <Descriptions.Item label="Phone">{cust?.phone || cust?.mobile || '—'}</Descriptions.Item>
            <Descriptions.Item label="Mobile">{cust?.mobile || '—'}</Descriptions.Item>
            <Descriptions.Item label="Registered shipping address" span={2}>
              <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                {formatCustomerShippingBlock(cust) || '—'}
              </Paragraph>
            </Descriptions.Item>
            <Descriptions.Item label="Registered billing address" span={2}>
              <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                {formatCustomerBillingBlock(cust) || '—'}
              </Paragraph>
            </Descriptions.Item>
            <Descriptions.Item label="Customer order">
              {dn.salesOrder?.id && dn.salesOrder?.sales_order_number ? (
                <Link to={`/growth/orders/${dn.salesOrder.id}`}>{dn.salesOrder.sales_order_number}</Link>
              ) : (
                dn.salesOrder?.sales_order_number || '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Customer PO (on DN)">{dn.customer_po_number || '—'}</Descriptions.Item>
            <Descriptions.Item label="Delivery note status">{dn.delivery_status || '—'}</Descriptions.Item>
            <Descriptions.Item label="Scheduled delivery date">
              {dn.delivery_date ? new Date(dn.delivery_date).toLocaleDateString() : '—'}
            </Descriptions.Item>
            {dn.delivery_time ? (
              <Descriptions.Item label="Delivery time">{dn.delivery_time}</Descriptions.Item>
            ) : null}
            <Descriptions.Item label="Ship-to / delivery address (this shipment)" span={2}>
              <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                {dn.delivery_address?.trim() || '—'}
              </Paragraph>
            </Descriptions.Item>
            <Descriptions.Item label="Shipping method">{dn.shipping_method || '—'}</Descriptions.Item>
            <Descriptions.Item label="Tracking">{dn.tracking_number || '—'}</Descriptions.Item>
            <Descriptions.Item label="Vehicle">{dn.vehicle_number || '—'}</Descriptions.Item>
            <Descriptions.Item label="Driver">{dn.driver_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Driver phone">{dn.driver_phone || '—'}</Descriptions.Item>
            {dn.special_instructions ? (
              <Descriptions.Item label="Special instructions" span={2}>
                <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{dn.special_instructions}</Paragraph>
              </Descriptions.Item>
            ) : null}
            {dn.notes ? (
              <Descriptions.Item label="Delivery note notes" span={2}>
                <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{dn.notes}</Paragraph>
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        </Card>
      ) : (
        <Card title="Customer & delivery">
          <Text type="secondary">
            No delivery note is linked to this IRN. Customer and line-level shipment details appear when the IRN is
            created from a delivery note.
          </Text>
        </Card>
      )}

      {dn && items.length > 0 ? (
        <Card title="Delivery note lines (items released on this shipment)">
          <Table<IrnDeliveryNoteItemSnapshot>
            rowKey={(r) => r.id ?? String(r.line_number)}
            size="small"
            pagination={false}
            scroll={{ x: 1100 }}
            columns={deliveryLineColumns}
            dataSource={items}
          />
        </Card>
      ) : null}

      {fir ? (
        <Card title="Final inspection (FIR) summary">
          <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="FIR number">
              {fir.id ? (
                <Link to={`/qms/final-inspection-reports/${fir.id}?source=fir_request`}>{fir.fir_number}</Link>
              ) : (
                fir.fir_number || '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="FIR status">{fir.status || '—'}</Descriptions.Item>
            <Descriptions.Item label="Inspection date">
              {fir.inspection_date ? new Date(fir.inspection_date).toLocaleDateString() : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Requested date">
              {fir.requested_date ? new Date(fir.requested_date).toLocaleDateString() : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="FIR approved qty">
              {fir.approved_quantity != null && !Number.isNaN(Number(fir.approved_quantity))
                ? Number(fir.approved_quantity).toFixed(4)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="FIR rejected qty">
              {fir.rejected_quantity != null && !Number.isNaN(Number(fir.rejected_quantity))
                ? Number(fir.rejected_quantity).toFixed(4)
                : '—'}
            </Descriptions.Item>
            {fir.rejection_reason ? (
              <Descriptions.Item label="FIR rejection reason" span={2}>
                {fir.rejection_reason}
              </Descriptions.Item>
            ) : null}
            {fir.workOrderLineItem ? (
              <Descriptions.Item label="FIR scope (work order line)" span={2}>
                Line {fir.workOrderLineItem.line_number ?? '—'}: {fir.workOrderLineItem.item_description || '—'}
                {fir.workOrderLineItem.item_code ? ` (${fir.workOrderLineItem.item_code})` : ''}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
          {fir.lineItemResults && fir.lineItemResults.length > 0 ? (
            <Table<IrnFirLineResultSnapshot>
              rowKey={(r) => r.id ?? String(r.workOrderLineItem?.line_number)}
              size="small"
              pagination={false}
              scroll={{ x: 900 }}
              columns={firLineColumns}
              dataSource={fir.lineItemResults}
            />
          ) : (
            <Text type="secondary">No per-line FIR results on record for this request.</Text>
          )}
        </Card>
      ) : null}
    </>
  );
}

function printPre(text: string | undefined | null): React.ReactNode {
  const raw = String(text ?? '');
  if (!raw.trim()) return <span>—</span>;
  return <div className="irn-print-pre">{raw}</div>;
}

/** Print layout: customer, shipment, DN lines, FIR summary (uses irn-print.css). */
export function IrnInspectorDetailsPrint({ irn }: { irn: InspectionReleaseNote }) {
  const dn = irn.deliveryNote;
  const cust = dn?.customer;
  const items = dn?.items ?? [];
  const fir = irn.fir;

  return (
    <>
      {dn ? (
        <div className="irn-print-section">
          <div className="irn-print-section-title">Customer & delivery (from delivery note)</div>
          <table className="irn-print-meta">
            <tbody>
              <tr>
                <th>Customer</th>
                <td colSpan={3}>
                  {cust?.customer_name || '—'}
                  {cust?.customer_code ? ` (${cust.customer_code})` : ''}
                </td>
              </tr>
              <tr>
                <th>Contact</th>
                <td>{cust?.contact_person || '—'}</td>
                <th>Email</th>
                <td>{cust?.email || '—'}</td>
              </tr>
              <tr>
                <th>Phone</th>
                <td>{cust?.phone || cust?.mobile || '—'}</td>
                <th>Mobile</th>
                <td>{cust?.mobile || '—'}</td>
              </tr>
              <tr>
                <th>Registered shipping</th>
                <td colSpan={3}>{printPre(formatCustomerShippingBlock(cust))}</td>
              </tr>
              <tr>
                <th>Registered billing</th>
                <td colSpan={3}>{printPre(formatCustomerBillingBlock(cust))}</td>
              </tr>
              <tr>
                <th>Customer order</th>
                <td>{dn.salesOrder?.sales_order_number || '—'}</td>
                <th>Customer PO</th>
                <td>{dn.customer_po_number || '—'}</td>
              </tr>
              <tr>
                <th>DN status</th>
                <td>{dn.delivery_status || '—'}</td>
                <th>Scheduled date</th>
                <td>{dn.delivery_date ? new Date(dn.delivery_date).toLocaleDateString() : '—'}</td>
              </tr>
              <tr>
                <th>Ship-to (this shipment)</th>
                <td colSpan={3}>{printPre((dn.delivery_address || '').trim() || '—')}</td>
              </tr>
              <tr>
                <th>Shipping method</th>
                <td>{dn.shipping_method || '—'}</td>
                <th>Tracking</th>
                <td>{dn.tracking_number || '—'}</td>
              </tr>
              <tr>
                <th>Vehicle</th>
                <td>{dn.vehicle_number || '—'}</td>
                <th>Driver / phone</th>
                <td>
                  {dn.driver_name || '—'}
                  {dn.driver_phone ? ` / ${dn.driver_phone}` : ''}
                </td>
              </tr>
              {dn.special_instructions ? (
                <tr>
                  <th>Special instructions</th>
                  <td colSpan={3}>{printPre(dn.special_instructions)}</td>
                </tr>
              ) : null}
              {dn.notes ? (
                <tr>
                  <th>DN notes</th>
                  <td colSpan={3}>{printPre(dn.notes)}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="irn-print-section">
          <div className="irn-print-section-title">Customer & delivery</div>
          <p className="irn-print-muted">No delivery note linked — customer and shipment lines are not available.</p>
        </div>
      )}

      {dn && items.length > 0 ? (
        <div className="irn-print-section">
          <div className="irn-print-section-title">Delivery note lines</div>
          <table className="irn-print-items-table">
            <thead>
              <tr>
                <th>Line</th>
                <th>Code</th>
                <th>Description</th>
                <th>Qty</th>
                <th>UOM</th>
                <th>HSN</th>
                <th>Batch</th>
                <th>Serial</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.id ?? `${it.line_number}-${idx}`}>
                  <td>{it.line_number ?? '—'}</td>
                  <td>{it.item_code || '—'}</td>
                  <td>{it.item_description || '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {it.delivered_quantity != null && !Number.isNaN(Number(it.delivered_quantity))
                      ? Number(it.delivered_quantity).toFixed(4)
                      : '—'}
                  </td>
                  <td>{it.uom || '—'}</td>
                  <td>{it.hsn_code || '—'}</td>
                  <td>{(it.batch_numbers || '').trim() || '—'}</td>
                  <td>{(it.serial_numbers || '').trim() || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {fir ? (
        <div className="irn-print-section">
          <div className="irn-print-section-title">Final inspection (FIR)</div>
          <table className="irn-print-meta">
            <tbody>
              <tr>
                <th>FIR number</th>
                <td>{fir.fir_number || '—'}</td>
                <th>Status</th>
                <td>{fir.status || '—'}</td>
              </tr>
              <tr>
                <th>Inspection date</th>
                <td>{fir.inspection_date ? new Date(fir.inspection_date).toLocaleDateString() : '—'}</td>
                <th>Approved / rejected qty</th>
                <td>
                  {fir.approved_quantity != null ? Number(fir.approved_quantity).toFixed(4) : '—'} /{' '}
                  {fir.rejected_quantity != null ? Number(fir.rejected_quantity).toFixed(4) : '—'}
                </td>
              </tr>
              {fir.rejection_reason ? (
                <tr>
                  <th>FIR rejection</th>
                  <td colSpan={3}>{printPre(fir.rejection_reason)}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {fir.lineItemResults && fir.lineItemResults.length > 0 ? (
            <table className="irn-print-items-table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>WO line</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Approved</th>
                  <th>Rejected</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {fir.lineItemResults.map((r, idx) => (
                  <tr key={r.id ?? idx}>
                    <td>{r.workOrderLineItem?.line_number ?? '—'}</td>
                    <td>{r.workOrderLineItem?.item_code || '—'}</td>
                    <td>{r.workOrderLineItem?.item_description || '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {r.approved_quantity != null ? Number(r.approved_quantity).toFixed(4) : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {r.rejected_quantity != null ? Number(r.rejected_quantity).toFixed(4) : '—'}
                    </td>
                    <td>{(r.rejection_reason || '').trim() || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
